import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useStations, useOrders } from "../hooks/useOrders";
import { useKitchenRealtime } from "../hooks/useKitchenRealtime";
import { useKitchenStore } from "../stores/kitchen.store";
import StationColumn from "../components/StationColumn";
import TableBoard from "./TableBoard";
import { Volume2, VolumeX, LogOut, RotateCw, Lock, LockOpen } from "lucide-react";
import { clientConfigApi, foodBlockApi } from "../api/client";

export default function ChefOverview() {
  const navigate = useNavigate();
  const clientId = useKitchenStore((s) => s.clientId);
  const jwtToken = useKitchenStore((s) => s.jwtToken);
  const userName = useKitchenStore((s) => s.userName);
  const audioEnabled = useKitchenStore((s) => s.audioEnabled);
  const toggleAudio = useKitchenStore((s) => s.toggleAudio);
  const logout = useKitchenStore((s) => s.logout);
  const stationOrder = useKitchenStore((s) => s.stationOrder);
  const setStationOrder = useKitchenStore((s) => s.setStationOrder);
  const moveStation = useKitchenStore((s) => s.moveStation);
  const viewMode = useKitchenStore((s) => s.viewMode);
  const setViewMode = useKitchenStore((s) => s.setViewMode);
  // Feature 113 — blocco ordini cibo
  const foodBlock = useKitchenStore((s) => s.foodBlock);
  const setFoodBlock = useKitchenStore((s) => s.setFoodBlock);
  const [foodBlockSaving, setFoodBlockSaving] = useState(false);

  // Connect real-time WebSocket → React Query bridge
  useKitchenRealtime(jwtToken);

  // Feature 113 — stato iniziale del blocco dal client-config (on mount).
  // Gli aggiornamenti successivi arrivano via WS (food_block_changed).
  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;
    clientConfigApi
      .get(clientId)
      .then((cfg) => {
        if (cancelled) return;
        setFoodBlock({
          enabled: !!cfg.foodOrderBlockEnabled,
          active: !!cfg.foodOrderBlockActive,
        });
      })
      .catch(() => {
        /* fallback: lascia lo stato di default (toggle nascosto) */
      });
    return () => {
      cancelled = true;
    };
  }, [clientId, setFoodBlock]);

  const handleToggleFoodBlock = async () => {
    if (!clientId || foodBlockSaving) return;
    const next = !foodBlock.active;
    setFoodBlockSaving(true);
    // Optimistic: aggiorna subito; il WS confermerà.
    setFoodBlock({ enabled: foodBlock.enabled, active: next });
    try {
      await foodBlockApi.toggle(clientId, next);
    } catch {
      // Rollback in caso di errore.
      setFoodBlock({ enabled: foodBlock.enabled, active: !next });
    } finally {
      setFoodBlockSaving(false);
    }
  };

  const { data: stations } = useStations(clientId);
  const { data: orders } = useOrders(clientId);

  // Init stationOrder when stations load (only if not already set)
  useEffect(() => {
    if (stations && stations.length > 0) {
      const stationIds = stations.map((s: any) => s.id);
      const hasAll = stationOrder.length === stationIds.length &&
        stationOrder.every((id: string) => stationIds.includes(id));
      if (!hasAll) {
        // Preserve existing order for known stations, append new ones
        const known = stationOrder.filter((id: string) => stationIds.includes(id));
        const newIds = stationIds.filter((id: string) => !known.includes(id));
        setStationOrder([...known, ...newIds]);
      }
    }
  }, [stations]);

  // Sort stations according to saved order
  const sortedStations = useMemo(() => {
    if (!stations || stations.length === 0) return [];
    if (stationOrder.length === 0) return stations;
    return [...stations].sort((a: any, b: any) => {
      const ai = stationOrder.indexOf(a.id);
      const bi = stationOrder.indexOf(b.id);
      return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
    });
  }, [stations, stationOrder]);

  // If not authenticated, redirect to login
  if (!jwtToken) {
    navigate("/login");
    return null;
  }

  if (!clientId) {
    navigate("/login");
    return null;
  }

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    // 2026-05-24 — Fix overflow vista chef webview: la pagina deve occupare
    // ESATTAMENTE il viewport (h-screen) senza scroll del documento. Lo scroll
    // diventa responsabilita' di ogni StationColumn → ogni colonna scrolla
    // indipendentemente. Prima: min-h-screen + grid auto = la pagina cresceva
    // con la colonna piu' alta e gli order item andavano off-screen.
    <div className="h-screen flex flex-col bg-kds-bg p-4 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 flex-shrink-0">
        <h1 className="text-order-num text-kds-text">Chef Overview</h1>
        <div className="flex items-center gap-4">
          <span className="text-kds-muted text-sm">
            {orders?.length ?? 0} ordini attivi
          </span>

          {/* Segmented toggle: Per postazione / Per tavolo */}
          <div className="flex rounded-lg bg-kds-surface overflow-hidden border border-kds-muted/20">
            <button
              onClick={() => setViewMode("station")}
              className={[
                "px-3 py-1.5 text-sm font-semibold transition-colors",
                viewMode === "station"
                  ? "bg-kds-card text-kds-text"
                  : "text-kds-muted hover:text-kds-text",
              ].join(" ")}
              title="Vista per postazione (default)"
            >
              Per postazione
            </button>
            <button
              onClick={() => setViewMode("table")}
              className={[
                "px-3 py-1.5 text-sm font-semibold transition-colors",
                viewMode === "table"
                  ? "bg-kds-card text-kds-text"
                  : "text-kds-muted hover:text-kds-text",
              ].join(" ")}
              title="Vista per tavolo"
            >
              Per tavolo
            </button>
          </div>

          {/* Feature 113 — Toggle blocco ordini cibo (visibile solo se la
              funzionalità è disponibile per il locale). */}
          {foodBlock.enabled && (
            <button
              onClick={handleToggleFoodBlock}
              disabled={foodBlockSaving}
              className={[
                "flex items-center gap-1.5 px-3 py-2 rounded-lg font-semibold min-h-touch transition-colors disabled:opacity-60",
                foodBlock.active
                  ? "bg-red-600 text-white hover:bg-red-700"
                  : "bg-kds-card text-kds-text hover:bg-kds-card/70",
              ].join(" ")}
              title={
                foodBlock.active
                  ? "Blocco ordini cibo ATTIVO — clicca per sbloccare"
                  : "Blocca gli ordini cibo dei camerieri"
              }
            >
              {foodBlock.active ? <Lock size={18} /> : <LockOpen size={18} />}
              {foodBlock.active ? "Cibo bloccato" : "Blocco cibo"}
            </button>
          )}

          <button
            onClick={() => window.location.reload()}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-kds-card text-kds-text font-semibold min-h-touch hover:bg-kds-card/70 transition-colors"
            title="Aggiorna la schermata"
          >
            <RotateCw size={18} />
            Aggiorna
          </button>

          <button
            onClick={toggleAudio}
            className="p-2 rounded-lg bg-kds-surface min-h-touch min-w-touch flex items-center justify-center"
            title={audioEnabled ? "Disattiva audio" : "Attiva audio"}
          >
            {audioEnabled ? <Volume2 size={18} /> : <VolumeX size={18} className="text-kds-muted" />}
          </button>
          <div className="w-3 h-3 rounded-full bg-kds-ready animate-pulse" />
          {userName && (
            <span className="text-kds-muted text-sm">{userName}</span>
          )}
          <button
            onClick={handleLogout}
            className="p-2 rounded-lg bg-kds-surface hover:bg-red-500/20 min-h-touch min-w-touch flex items-center justify-center transition-colors"
            title="Esci"
          >
            <LogOut size={18} className="text-kds-muted" />
          </button>
        </div>
      </div>

      {/* Main content area — switches between station view and table view */}
      {viewMode === "table" ? (
        // Per-table view: renders TableBoard which groups by tableId
        // useKitchenRealtime stays mounted above so realtime works for both views.
        <TableBoard clientId={clientId} />
      ) : (
        /* Station columns — flex-1 + overflow-hidden + auto-rows-fr fa si'
           che il grid occupi tutta l'altezza residua del viewport e ogni
           row del grid abbia altezza uguale, cosi' h-full sulle colonne
           (StationColumn) funziona per lo scroll interno. */
        sortedStations.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 flex-1 overflow-hidden auto-rows-fr">
            {sortedStations.map((station: any, idx: number) => (
              <StationColumn
                key={station.id}
                station={station}
                orders={(orders ?? [])
                  .filter(
                    (o: any) =>
                      o.status !== "COMPLETED" &&
                      o.status !== "CANCELLED" &&
                      o.items?.some(
                        (i: any) =>
                          i.stationId === station.id &&
                          i.status !== "SERVED" &&
                          i.status !== "CANCELLED",
                      ),
                  )}
                canMoveLeft={idx > 0}
                canMoveRight={idx < sortedStations.length - 1}
                onMoveLeft={() => moveStation(station.id, "left")}
                onMoveRight={() => moveStation(station.id, "right")}
              />
            ))}
          </div>
        ) : (
          <div className="text-center text-kds-muted py-20">
            <p className="text-xl">Nessuna stazione configurata</p>
            <p className="mt-2">Configura le stazioni dal CRM</p>
          </div>
        )
      )}
    </div>
  );
}
